import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { ConfigService } from '../services/config.service';
import { Observable, catchError, filter, race, switchMap, take, throwError, timer } from 'rxjs';
import { AuthService } from '../services/auth.service';

// Refresh this many seconds BEFORE the access token expires, so a request never has to fail first.
const REFRESH_LEEWAY_SECONDS = 60;

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn) => {

  const configService = inject(ConfigService);
  const authService = inject(AuthService);

  const send = (request: HttpRequest<any>): Observable<any> => {
    request = setReqHeader(request, configService);

    return next(request).pipe(
      catchError((error) => {
        // Safety net (clock skew, token revoked server side): refresh after the rejection.
        if (!request.url.includes('/auth') && error.status === 403 && error.error?.message === 'Forbidden') {

          const timeout$ = timer(3000).pipe(
            switchMap(() => throwError(() => new Error('Refresh token timeout'))),
          );

          const refreshToken$ = authService.refreshToken().pipe(
            filter((token) => token !== null), // Tunggu sampai token baru tersedia
            take(1),
            // Retry request dengan token baru
            switchMap(() => next(setReqHeader(request, configService))),
            catchError((err) => {
              console.error('Refresh Token Failed:', err);
              authService.logout(); // Logout jika refresh token gagal
              return throwError(() => new Error('REFRESH_TOKEN_FAILED'));
            }),
          );

          return race([refreshToken$, timeout$]);
        }
        return throwError(() => error);
      })
    );
  };

  // Access token is (almost) expired: refresh first, then send once with the new token, instead of
  // sending a request that will be rejected and only then refreshing. Concurrent requests share the
  // same refresh call, and a failed refresh is detected after one call, not after a failed request.
  const expiresIn = authService.getTokenExpiresIn();

  if (isApiUrl(req.url) && !req.url.includes('/auth') && expiresIn !== null && expiresIn < REFRESH_LEEWAY_SECONDS) {
    return authService.refreshToken().pipe(
      take(1),
      catchError((err) => {
        console.error('Refresh Token Failed:', err);
        authService.logout();
        return throwError(() => new Error('REFRESH_TOKEN_FAILED'));
      }),
      switchMap(() => send(req)),
    );
  }

  return send(req);
};

const whitelistedUrl = [
  'http://localhost',
  'https://api.sokrates.xyz',
  'https://api.sokrates.co.id',
];

function isApiUrl(url: string): boolean {
  return whitelistedUrl.some((item) => url.includes(item));
}

function setReqHeader(req: HttpRequest<any>, configService: ConfigService): HttpRequest<any> {
  const token = configService.getToken();
  const tenantUuid = configService.getTenantUuid();

  if (isApiUrl(req.url)) {
    const headers = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (req.url.includes('recruitment-http')) {
      headers['tenant-uuid'] = tenantUuid;
    }

    return req.clone({
      setHeaders: headers,
    });
  }

  return req;
}
